'use client';

import { useEffect } from 'react';
import { SWEETLINK_AUTO_KEY, SWEETLINK_AUTO_RESUME_FROM_KEY } from '@/lib/sweetlink/constants';
import { setSweetLinkStatusSnapshot } from '@/lib/sweetlink/status-store';
import { type BrowserWindow, getBrowserWindow } from '@/lib/utils/browser-window';

// Watches for the CLI bootstrap signal (sessionStorage flag or custom event) and ensures we only attempt the
// SweetLink handshake once per window, even if multiple components subscribe simultaneously.

type AutoBootstrapWindow = BrowserWindow & { __sweetlinkCliAuto?: boolean; __sweetlinkCliAutoInFlight?: boolean };

function setCliAutoFlag(value: 'pending' | 'resume' = 'pending'): void {
  try {
    globalThis.sessionStorage?.setItem(SWEETLINK_AUTO_KEY, value);
  } catch {
    /* ignore sessionStorage access issues */
  }
}

function clearCliAutoFlag(): void {
  try {
    globalThis.sessionStorage?.removeItem(SWEETLINK_AUTO_KEY);
    globalThis.sessionStorage?.removeItem(SWEETLINK_AUTO_RESUME_FROM_KEY);
  } catch {
    /* ignore sessionStorage access issues */
  }
}

function hasCliAutoFlag(): boolean {
  try {
    return globalThis.sessionStorage?.getItem(SWEETLINK_AUTO_KEY) === 'pending';
  } catch {
    return false;
  }
}

function hasResumeFlag(): boolean {
  try {
    return globalThis.sessionStorage?.getItem(SWEETLINK_AUTO_KEY) === 'resume';
  } catch {
    return false;
  }
}

export function useSweetLinkAutoBootstrap(): void {
  const bootstrapWindowInitial = getBrowserWindow() as AutoBootstrapWindow | null;

  if (bootstrapWindowInitial) {
    try {
      const currentUrl = new URL(bootstrapWindowInitial.location.href);
      if (currentUrl.searchParams.get('sweetlink') === 'auto' || hasCliAutoFlag() || hasResumeFlag()) {
        bootstrapWindowInitial.__sweetlinkCliAuto = true;
      }
    } catch {
      /* ignore */
    }
  }

  useEffect(() => {
    const runtimeWindow = getBrowserWindow() as AutoBootstrapWindow | null;
    const bootstrapWindow = runtimeWindow;
    if (!bootstrapWindow) {
      return;
    }
    const scheduleResumeRedirect = () => {
      try {
        const resumeTarget = bootstrapWindow.sessionStorage?.getItem(SWEETLINK_AUTO_RESUME_FROM_KEY);
        if (resumeTarget && resumeTarget !== bootstrapWindow.location.href) {
          if (typeof console?.info === 'function') {
            console.info('[SweetLink CLI] redirecting to resume target', {
              resumeTarget,
              href: bootstrapWindow.location.href,
            });
          }
          bootstrapWindow.location.replace(resumeTarget);
          return true;
        }
      } catch {
        /* ignore sessionStorage access issues */
      }
      return false;
    };

    let resumeRedirectPoll: number | null = null;
    const startResumeRedirectWatcher = () => {
      if (resumeRedirectPoll !== null) {
        return;
      }
      resumeRedirectPoll = bootstrapWindow.setInterval(() => {
        if (scheduleResumeRedirect() && resumeRedirectPoll !== null) {
          bootstrapWindow.clearInterval(resumeRedirectPoll);
          resumeRedirectPoll = null;
        }
      }, 200);
    };

    if (scheduleResumeRedirect()) {
      return () => {
        if (resumeRedirectPoll !== null) {
          bootstrapWindow.clearInterval(resumeRedirectPoll);
        }
      };
    }
    startResumeRedirectWatcher();

    if (typeof console?.info === 'function') {
      console.info('[SweetLink CLI] auto-bootstrap hook attached');
    }

    const runActivation = (options?: { fromResume?: boolean }) => {
      if (bootstrapWindow.__sweetlinkCliAutoInFlight) {
        return;
      }

      bootstrapWindow.__sweetlinkCliAutoInFlight = true;
      if (options?.fromResume) {
        try {
          const resolver = bootstrapWindow.sessionStorage?.getItem(SWEETLINK_AUTO_RESUME_FROM_KEY);
          if (!resolver || resolver === bootstrapWindow.location.href) {
            bootstrapWindow.sessionStorage?.setItem(SWEETLINK_AUTO_KEY, 'pending');
            bootstrapWindow.sessionStorage?.removeItem(SWEETLINK_AUTO_RESUME_FROM_KEY);
          }
        } catch {
          /* ignore sessionStorage access issues */
        }
      }
      setCliAutoFlag('pending');
      setSweetLinkStatusSnapshot({ status: 'connecting', reason: null, codename: null });

      const activate = async () => {
        try {
          const sweetlinkModule = await import('@/client/sweetlink/activate');
          if (typeof console?.info === 'function') {
            console.info('[SweetLink CLI] auto-bootstrap starting session activation');
          }
          await sweetlinkModule.ensureSweetLinkSession({ copyToClipboard: false });
          const currentUrl = new URL(bootstrapWindow.location.href);
          if (currentUrl.searchParams.has('sweetlink')) {
            currentUrl.searchParams.delete('sweetlink');
            bootstrapWindow.history.replaceState(bootstrapWindow.history.state, '', currentUrl.toString());
          }
        } catch (error: unknown) {
          console.error('SweetLink auto-activation failed', error);
          const message = error instanceof Error ? error.message : 'Unknown SweetLink error';
          setSweetLinkStatusSnapshot({ status: 'error', reason: message, codename: null });
        } finally {
          clearCliAutoFlag();
          bootstrapWindow.__sweetlinkCliAutoInFlight = false;
        }
      };

      bootstrapWindow.setTimeout(() => {
        activate().catch(() => {
          // activation owns error reporting
        });
      }, 0);
    };

    const handleCliSignal = (event?: Event) => {
      bootstrapWindow.__sweetlinkCliAuto = true;
      const resumeFlag = hasResumeFlag();
      const fromResume = resumeFlag || event instanceof CustomEvent;
      runActivation({ fromResume });
    };

    const currentUrl = new URL(bootstrapWindow.location.href);
    const shouldAutoBootstrap = currentUrl.searchParams.get('sweetlink') === 'auto' || hasCliAutoFlag();

    if (shouldAutoBootstrap) {
      bootstrapWindow.__sweetlinkCliAuto = true;
      runActivation({ fromResume: false });
    }

    bootstrapWindow.addEventListener('sweetlink:cli-auto', handleCliSignal);
    return () => {
      if (resumeRedirectPoll !== null) {
        bootstrapWindow.clearInterval(resumeRedirectPoll);
      }
      bootstrapWindow.removeEventListener('sweetlink:cli-auto', handleCliSignal);
    };
  }, []);
}
